import { escapeCsvCell } from '@/core/lib/csv-cell';
import type {
  TemplateMemberValueColumn,
  TemplateMemberValuesEntry,
  TemplateMemberValuesMap,
} from '@/core/lib/profile-template-member-values';
import {
  getMemberCellValue,
  setMemberCellValue,
} from '@/core/lib/profile-template-member-values';

export type MemberRosterRow = {
  userId: string;
  displayName: string;
  email?: string;
};

const CSV_DELIMITER = ';';

function parseCsvLine(line: string, delimiter: string): string[] {
  const cells: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += ch;
      }
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      continue;
    }
    if (ch === delimiter) {
      cells.push(current);
      current = '';
      continue;
    }
    current += ch;
  }
  cells.push(current);
  return cells;
}

function countDelimiterOutsideQuotes(line: string, delimiter: string): number {
  let count = 0;
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        i++;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }
    if (!inQuotes && ch === delimiter) {
      count++;
    }
  }
  return count;
}

function detectCsvDelimiter(headerLine: string): string {
  const semicolonCount = countDelimiterOutsideQuotes(headerLine, ';');
  const commaCount = countDelimiterOutsideQuotes(headerLine, ',');
  if (commaCount > semicolonCount) return ',';
  return CSV_DELIMITER;
}

function findCsvHeaderLine(lines: string[]): {
  headerLineIndex: number;
  delimiter: string;
  headerCells: string[];
} | null {
  for (let i = 0; i < lines.length; i++) {
    const delimiter = detectCsvDelimiter(lines[i]!);
    const headerCells = parseCsvLine(lines[i]!, delimiter);
    const userIdIndex = headerCells.findIndex(
      (h) => h.trim().toLowerCase() === 'userid',
    );
    if (userIdIndex >= 0) {
      return { headerLineIndex: i, delimiter, headerCells };
    }
  }
  return null;
}

function columnHeader(column: TemplateMemberValueColumn): string {
  if (column.kind === 'field') return column.key;
  if (column.kind === 'social') return `social:${column.platform}`;
  if (column.kind === 'moduleEntry')
    return column.label.trim() || column.entryId;
  return `setting:${column.key}`;
}

function parseColumnHeader(
  header: string,
  columns: TemplateMemberValueColumn[],
): TemplateMemberValueColumn | null {
  const normalized = header.trim().toLowerCase();
  for (const col of columns) {
    if (columnHeader(col).toLowerCase() === normalized) return col;
    // Backward compatibility: support old field:* CSV headers.
    if (
      col.kind === 'field' &&
      `field:${col.key}`.toLowerCase() === normalized
    ) {
      return col;
    }
    // Backward compatibility: support old moduleEntry:* CSV headers.
    if (
      col.kind === 'moduleEntry' &&
      `moduleEntry:${col.entryId}`.toLowerCase() === normalized
    ) {
      return col;
    }
  }
  const labelMatch = columns.find(
    (c) => c.label.trim().toLowerCase() === normalized,
  );
  return labelMatch ?? null;
}

function columnHint(column: TemplateMemberValueColumn): string {
  if (column.kind === 'setting') {
    if (column.inputType === 'boolean') return 'Erlaubte Werte: true | false';
    const optionValues = (column.options ?? [])
      .map((option) => {
        const value = option.value.trim();
        const label = option.label.trim();
        if (!value) return '';
        if (label && label.toLowerCase() !== value.toLowerCase()) {
          return `${value} (${label})`;
        }
        return value;
      })
      .filter(Boolean);
    if (optionValues.length > 0) {
      return `Erlaubte Werte: ${optionValues.join(' | ')}`;
    }
    return 'Erlaubte Werte: Enum-Wert';
  }

  if (column.kind === 'moduleEntry') {
    if (column.inputType === 'email') return 'Erlaubt: E-Mail-Text';
    if (column.inputType === 'phone') return 'Erlaubt: Telefon-Text';
  }

  return 'Erlaubt: Freitext';
}

export function exportMemberValuesCsv(params: {
  roster: MemberRosterRow[];
  memberValues: TemplateMemberValuesMap;
  columns: TemplateMemberValueColumn[];
}): string {
  return exportMemberValuesCsvFromValues({
    roster: params.roster,
    values: params.memberValues,
    columns: params.columns,
    getCellValue: (entry, col) =>
      getMemberCellValue(typeof entry === 'string' ? undefined : entry, col),
  });
}

export function exportMemberValuesCsvFromValues(params: {
  roster: MemberRosterRow[];
  values: Record<string, string | TemplateMemberValuesEntry | undefined>;
  columns: TemplateMemberValueColumn[];
  getCellValue: (
    entry: string | TemplateMemberValuesEntry | undefined,
    column: TemplateMemberValueColumn,
  ) => string;
}): string {
  const headers = [
    'userId',
    'displayName',
    ...params.columns.map((c) => columnHeader(c)),
  ];
  const hints = [
    'Hinweis: Nicht aendern (Zuordnung beim Import)',
    'Hinweis: Nur Anzeige (beim Import ignoriert)',
    ...params.columns.map((c) => columnHint(c)),
  ];
  const lines = [
    hints.map((cell) => escapeCsvCell(cell, CSV_DELIMITER)).join(CSV_DELIMITER),
    '',
    headers
      .map((cell) => escapeCsvCell(cell, CSV_DELIMITER))
      .join(CSV_DELIMITER),
  ];

  for (const row of params.roster) {
    const entry = params.values[row.userId];
    const cells = [
      row.userId,
      row.displayName,
      ...params.columns.map((col) => params.getCellValue(entry, col)),
    ];
    lines.push(
      cells
        .map((cell) => escapeCsvCell(cell, CSV_DELIMITER))
        .join(CSV_DELIMITER),
    );
  }
  return `\uFEFF${lines.join('\n')}\n`;
}

export function importMemberValuesCsvToUpdates(params: {
  csvText: string;
  roster: MemberRosterRow[];
  columns: TemplateMemberValueColumn[];
  existing: Record<string, string>;
  defaultValue: string;
}): Record<string, string> {
  const lines = params.csvText
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length < 2) return {};

  const headerInfo = findCsvHeaderLine(lines);
  if (!headerInfo) {
    throw new Error('missingUserIdColumn');
  }
  const { headerLineIndex, delimiter, headerCells } = headerInfo;
  const columnByIndex: (TemplateMemberValueColumn | null)[] = headerCells.map(
    (h) => parseColumnHeader(h, params.columns),
  );

  const userIdIndex = headerCells.findIndex(
    (h) => h.trim().toLowerCase() === 'userid',
  );
  if (userIdIndex < 0) {
    throw new Error('missingUserIdColumn');
  }

  const rosterIds = new Set(params.roster.map((r) => r.userId));
  const standard = params.defaultValue.trim();
  const updates: Record<string, string> = {};

  for (let i = headerLineIndex + 1; i < lines.length; i++) {
    const cells = parseCsvLine(lines[i]!, delimiter);
    const userId = (cells[userIdIndex] ?? '').trim();
    if (!userId || !rosterIds.has(userId)) continue;

    for (let c = 0; c < columnByIndex.length; c++) {
      const column = columnByIndex[c];
      if (!column) continue;
      const raw = (cells[c] ?? '').trim();
      const existing = (params.existing[userId] ?? standard).trim();
      const next = raw === '' || raw === standard ? standard : raw;
      if (next === existing) continue;
      updates[userId] = next === standard ? '' : next;
    }
  }

  return updates;
}

export function importMemberValuesCsv(params: {
  csvText: string;
  roster: MemberRosterRow[];
  columns: TemplateMemberValueColumn[];
  existing: TemplateMemberValuesMap;
}): TemplateMemberValuesMap {
  const lines = params.csvText
    .replace(/^\uFEFF/, '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);
  if (lines.length < 2) return params.existing;

  const headerInfo = findCsvHeaderLine(lines);
  if (!headerInfo) {
    throw new Error('missingUserIdColumn');
  }
  const { headerLineIndex, delimiter, headerCells } = headerInfo;
  const columnByIndex: (TemplateMemberValueColumn | null)[] = headerCells.map(
    (h) => parseColumnHeader(h, params.columns),
  );

  const userIdIndex = headerCells.findIndex(
    (h) => h.trim().toLowerCase() === 'userid',
  );
  if (userIdIndex < 0) {
    throw new Error('missingUserIdColumn');
  }

  const rosterIds = new Set(params.roster.map((r) => r.userId));
  const next: TemplateMemberValuesMap = { ...params.existing };

  for (let i = headerLineIndex + 1; i < lines.length; i++) {
    const cells = parseCsvLine(lines[i]!, delimiter);
    const userId = (cells[userIdIndex] ?? '').trim();
    if (!userId || !rosterIds.has(userId)) continue;

    let entry: TemplateMemberValuesEntry = { ...(next[userId] ?? {}) };
    for (let c = 0; c < columnByIndex.length; c++) {
      const col = columnByIndex[c];
      if (!col) continue;
      const raw = cells[c] ?? '';
      entry = setMemberCellValue(entry, col, raw, {
        normalizeWhitespace: true,
      });
    }
    const hasData =
      entry.jobTitle != null ||
      entry.company != null ||
      entry.location != null ||
      (entry.socials && Object.keys(entry.socials).length > 0) ||
      (entry.moduleEntries &&
        Object.values(entry.moduleEntries).some(
          (v) => v != null && v !== '',
        )) ||
      (entry.settings &&
        Object.values(entry.settings).some((v) => v != null && v !== ''));
    if (hasData) {
      next[userId] = entry;
    } else {
      delete next[userId];
    }
  }

  return next;
}
